/* eslint-disable max-lines */
import { Controller } from "stimulus";
import dispatchCustomEvent from "../../functions/dispatchCustomEvent.js";

export default class extends Controller {
  static get targets() {
    return ["checkbox", "selectAllCheckbox", "hiddenField"];
  }

  static get values() {
    return { store: Boolean };
  }

  connect() {
    this._preventAutoComplete();
    this._updateSelectAllDisabledState();
    if (this.saveToStore) this._registerWithStore();
  }

  updateSelectAll(event) {
    this._toggleSelectAllCheckbox();

    this._updateHiddenField(event);
    if (this.saveToStore) this._syncWithStore();
  }

  // Store methods

  _registerWithStore() {
    this.dispatch("register", {
      detail: { id: this.element.id, values: [] },
      prefix: false,
    });
  }

  _syncWithStore() {
    this.dispatch("sync", {
      detail: { id: this.element.id, values: this.hiddenFieldValueArr },
      prefix: false,
    });
  }

  updateFromStore({ detail: { id, values } }) {

    if (id !== this.element.id) return;

    this.checkboxTargets.forEach((checkbox) => {
      checkbox.checked = values.includes(checkbox.dataset.id);
    });

    this._toggleSelectAllCheckbox();

    if (this.hasHiddenFieldTarget) {
      this.hiddenFieldTarget.value = values.join(",");
      dispatchCustomEvent(this.hiddenFieldTarget, "change");
    }
  }

  onSelectAll() {
    if (this.selectAllCheckboxTarget.checked) {
      this._selectAllCheckboxes();
      this._addValue();
    } else {
      this.selectedCheckboxValues.forEach((scv) => this._removeValue(scv));
      this._unSelectAllCheckboxes();
    }

    if (this.hasHiddenFieldTarget)
      dispatchCustomEvent(this.hiddenFieldTarget, "change");
    if (this.saveToStore) this._syncWithStore();
  }

  _toggleSelectAllCheckbox() {
    if (this.allCheckboxesChecked) {
      this._selectSelectAllCheckbox();
    } else {
      this._unselectSelectAllCheckbox();
    }
  }

  _updateHiddenField(event) {
    if (!this.hasHiddenFieldTarget) return;

    if (event.target.checked) {
      this._addValue();
    } else {
      this._removeValue(event.target.id);
    }

    dispatchCustomEvent(this.hiddenFieldTarget, "change");
  }

  _removeValue(valueToRemove) {
    if (!this.hasHiddenFieldTarget) return;

    const amendedValues = this.hiddenFieldValueArr.filter((a) => a !== valueToRemove);
    this.hiddenFieldTarget.value = amendedValues.join(",");
  }

  _addValue() {
    if (!this.hasHiddenFieldTarget) return;

    let selectedValues = new Set(this.selectedCheckboxValues);

    if (this.hiddenFieldTarget.value.length) {
      const hiddenFieldValueSet = new Set(this.hiddenFieldValueArr);
      selectedValues = selectedValues.union(hiddenFieldValueSet);
    }

    this.hiddenFieldTarget.value = [...selectedValues].join(",");
  }

  _selectSelectAllCheckbox() {
    this.selectAllCheckboxTarget.checked = true;
  }

  _unselectSelectAllCheckbox() {
    this.selectAllCheckboxTarget.checked = false;
  }

  _selectAllCheckboxes() {
    this.checkboxTargets.forEach((checkbox) => {
      if (!checkbox.disabled) {
        checkbox.checked = true;
      }
    });
  }

  _unSelectAllCheckboxes() {
    this.checkboxTargets.forEach((checkbox) => {
      checkbox.checked = false;
    });
  }

  _updateSelectAllDisabledState() {
    if (!this.hasSelectAllCheckboxTarget) return;

    const enabledCheckboxes = this.checkboxTargets.filter((checkbox) => !checkbox.disabled);
    this.selectAllCheckboxTarget.disabled = enabledCheckboxes.length === 0;

    if (this.selectAllCheckboxTarget.disabled) {
      this.selectAllCheckboxTarget.checked = false;
    }
  }

  get selectedCheckboxValues() {
    return this.selectedCheckboxes.map((checkbox) => {
      return checkbox.dataset.id;
    });
  }

  get selectedCheckboxes() {
    return this.checkboxTargets.filter((checkbox) => {
      return checkbox.checked;
    });
  }

  get allCheckboxesChecked() {
    const enabledCheckboxes = this.checkboxTargets.filter((checkbox) => !checkbox.disabled);
    return (
      enabledCheckboxes.length > 0 &&
      enabledCheckboxes.every((checkbox) => checkbox.checked)
    );
  }

  get hiddenFieldValueArr() {
    if (this.hiddenFieldTarget.value === "") return [];

    return this.hiddenFieldTarget.value.split(",");
  }

  get saveToStore() {
    return this.hasStoreValue && this.storeValue;
  }

  _preventAutoComplete() {
    this.checkboxTargets.forEach((checkbox) => {
      checkbox.setAttribute("autocomplete", "off");
    });

    if (this.hasSelectAllCheckboxTarget) {
      this.selectAllCheckboxTarget.setAttribute("autocomplete", "off");
    }
  }
}
